import { useCallback, useEffect, useRef, useState } from "react";
import { renderToStaticMarkup, renderToString } from "react-dom/server";

import { clusterMarkerSizes, NaverClusterMarker } from "@locators/components/shared/components/NaverClusterMarker";
import { UserMarkerInfoWindow } from "@locators/components/shared/components/UserMarkerInfoWindow";

import { useConfig } from "@jti/app-context/useConfig";
import type { FlotiqStore } from "@jti/fe-common";

import { ACTIVE_MARKER, DEFAULT_MARKER_NAVER, USER_LOCATION } from "../../store-locator-icons";
import { getStoreLocatorMarkerIcon } from "../../utils";

import { handleExtendNaverMaps, MarkerClustering } from "./MarkerClustering";
import type { UseNaverMapsStoreLocator } from "./useNaverMapsStoreLocator.types";

type ClusterMarker = {
  eventTarget: {
    innerText: number | string;
  };
};

type MarkerClusteringInstance = {
  setMap: (map: naver.maps.Map | null) => void;
};
type MarkerClusteringConstructor = new (options: Record<string, unknown>) => MarkerClusteringInstance;

export const useNaverMapsStoreLocator: UseNaverMapsStoreLocator = ({
  defaultLat = "0",
  defaultLng = "0",
  handleSelectStore,
  activeStoreId,
  defaultZoom = 10,
  defaultStoreIcon,
  activeStoreIcon,
  isSearchVisible,
  handleLoadFlotiqStores,
}) => {
  const { mapProviderExternalJs } = useConfig();
  const [isMapLoading, setIsMapLoading] = useState(false);
  const [isMapError, setIsMapError] = useState(false);
  const [isSearchResultPending, setIsSearchResultPending] = useState(false);
  const markers = useRef<naver.maps.Marker[]>([]);
  const mapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const naverMapRef = useRef<naver.maps.Map | null>(null);
  const infoWindowRef = useRef<naver.maps.InfoWindow | null>(null);
  const userMarkerInfoWindow = useRef<naver.maps.InfoWindow | null>(null);
  const markerClusters = useRef<MarkerClusteringInstance | null>(null);
  const defaultMarkerIconRef = useRef(getStoreLocatorMarkerIcon(defaultStoreIcon ?? DEFAULT_MARKER_NAVER));
  const activeMarkerIconRef = useRef(getStoreLocatorMarkerIcon(activeStoreIcon ?? ACTIVE_MARKER));

  useEffect(() => {
    defaultMarkerIconRef.current = getStoreLocatorMarkerIcon(defaultStoreIcon ?? DEFAULT_MARKER_NAVER);
  }, [defaultStoreIcon]);

  useEffect(() => {
    activeMarkerIconRef.current = getStoreLocatorMarkerIcon(activeStoreIcon ?? ACTIVE_MARKER);
  }, [activeStoreIcon]);

  const deleteAllMarkers = () => {
    markerClusters.current?.setMap(null);

    markers.current.forEach((marker) => {
      marker.setMap(null);
    });
    markers.current = [];
    markerClusters.current = null;
    infoWindowRef.current?.close();
    handleSelectStore("");
  };

  const setDefaultIconForMarkers = useCallback(() => {
    markers.current.forEach((marker) => {
      marker.setIcon(defaultMarkerIconRef.current);
    });
  }, []);

  const handleMarkerClick = useCallback(
    (store: FlotiqStore, marker: naver.maps.Marker) => () => {
      naverMapRef.current?.setCenter(marker.getPosition());
      handleSelectStore(store.id);
      setDefaultIconForMarkers();
      marker.setIcon(activeMarkerIconRef.current);
    },
    [handleSelectStore, setDefaultIconForMarkers]
  );

  const handleLoadMarkers = (stores: FlotiqStore[]) => {
    const naverMap = naverMapRef.current;

    if (!naverMap) {
      return;
    }
    deleteAllMarkers();
    stores.forEach((store) => {
      const marker = new naver.maps.Marker({
        position: new naver.maps.LatLng(store.Location.lat, store.Location.lon),
        map: naverMap,
        title: store.id,
        icon: defaultMarkerIconRef.current,
      });

      markers.current = [...markers.current, marker];

      naver.maps.Event.addListener(marker, "click", handleMarkerClick(store, marker));
    });

    const clusterMarkers = clusterMarkerSizes.map((size) => {
      return {
        content: renderToString(<NaverClusterMarker type={size} />),
        size: new naver.maps.Size(40, 40),
        anchor: new naver.maps.Point(20, 20),
      };
    });

    const clusters = new (MarkerClustering as unknown as MarkerClusteringConstructor)({
      minClusterSize: 2,
      maxZoom: 13,
      map: naverMap,
      markers: markers.current,
      disableClickZoom: false,
      gridSize: 120,
      icons: clusterMarkers,
      indexGenerator: [10, 100, 200, 500, 1000],
      stylingFunction: function (clusterMarker: ClusterMarker, count: number) {
        clusterMarker.eventTarget.innerText = count;
      },
    });
    markerClusters.current = clusters;
  };

  const handleMapEvent = useCallback(() => {
    const naverMap = naverMapRef.current;

    if (!naverMap) {
      return;
    }

    setDefaultIconForMarkers();
    handleLoadFlotiqStores(naverMap);
  }, [handleLoadFlotiqStores, setDefaultIconForMarkers]);

  const handleCreateMap = useCallback(() => {
    handleExtendNaverMaps(naver);
    const lat = Number(defaultLat);
    const lng = Number(defaultLng);
    naverMapRef.current = new naver.maps.Map("storeLocatorMap", {
      center: new naver.maps.LatLng(lat, lng),
      zoom: defaultZoom,
      zoomControl: true,
    });

    naverMapRef?.current?.addListener("click", () => {
      if (infoWindowRef.current?.getMap()) {
        infoWindowRef.current?.close();
      }
      if (userMarkerInfoWindow.current?.getMap()) {
        userMarkerInfoWindow.current?.close();
      }
      setDefaultIconForMarkers();
      handleSelectStore("");
    });

    naverMapRef?.current?.addListener("zoom_changed", () => {
      handleMapEvent();
    });

    naverMapRef?.current?.addListener("resize", () => {
      handleMapEvent();
    });

    naverMapRef.current?.addListener("dragend", () => {
      handleMapEvent();
    });
    handleLoadFlotiqStores(naverMapRef.current);
  }, [
    defaultLat,
    defaultLng,
    defaultZoom,
    handleLoadFlotiqStores,
    handleMapEvent,
    handleSelectStore,
    setDefaultIconForMarkers,
  ]);

  const handleInitMap = useCallback(() => {
    setIsMapLoading(true);

    if (!mapProviderExternalJs) {
      setIsMapError(true);
      setIsMapLoading(false);
      return;
    }

    const head = document.head;
    const script = document.createElement("script");
    script.src = mapProviderExternalJs;

    script.type = "text/javascript";
    script.onload = () => {
      handleCreateMap();
      setIsMapLoading(false);
    };

    script.onerror = () => {
      setIsMapError(true);
      setIsMapLoading(false);
    };

    head.appendChild(script);
  }, [handleCreateMap, mapProviderExternalJs]);

  const handleLoadUserMarker = (userLat: number, userLng: number) => {
    const naverMap = naverMapRef.current;

    if (!naverMap) {
      return;
    }

    naverMap.setCenter(new naver.maps.LatLng(userLat, userLng));
    naverMap.setZoom(15);

    const infoWindowContent = renderToStaticMarkup(<UserMarkerInfoWindow />);

    userMarkerInfoWindow.current = new naver.maps.InfoWindow({
      content: infoWindowContent,
    });
    const userMarker = new naver.maps.Marker({
      position: new naver.maps.LatLng(userLat, userLng),
      map: naverMap,
      icon: {
        url: USER_LOCATION,
        scaledSize: new naver.maps.Size(30, 30),
      },
    });

    userMarker.addListener("click", () => {
      const infoWindow = userMarkerInfoWindow.current;

      if (!infoWindow) {
        return;
      }

      if (infoWindow.getMap()) {
        infoWindow.close();
      } else {
        infoWindow.open(naverMap, userMarker);
      }
    });
    userMarker.trigger("click");
  };

  const handleInputSearchClick = () => {
    setIsSearchResultPending(true);
    if (inputRef.current && naver.maps?.Service?.geocode) {
      const searchValue = inputRef.current.value;
      if (searchValue) {
        naver.maps.Service.geocode({ query: searchValue }, (status, response) => {
          if (status === naver.maps.Service.Status.ERROR) {
            console.error("Something went wrong. Please try again.");
            return;
          }
          const { v2 } = response;

          if (v2) {
            const { addresses } = v2;
            if (addresses.length > 0) {
              const { x, y } = addresses[0];
              naverMapRef.current?.setCenter(new naver.maps.LatLng(Number(y), Number(x)));
              naverMapRef.current?.setZoom(10);
            }
          }
          setIsSearchResultPending(false);
        });
      }
    }
  };

  const handleSelectStoreFromList = useCallback(
    (storeId: string) => {
      const activeStore = markers.current.find((marker) => marker.getTitle() === storeId);

      if (activeStore) {
        activeStore.trigger("click");
        return;
      }

      handleSelectStore(storeId);
    },
    [handleSelectStore]
  );

  useEffect(() => {
    handleInitMap();
  }, [handleInitMap]);

  useEffect(() => {
    if (activeStoreId) {
      const activeStore = markers.current.find((marker) => marker.getTitle() === activeStoreId);
      if (activeStore) {
        activeStore.trigger("click");
      }
    }
  }, [activeStoreId, markers]);

  return {
    isSearchVisible,
    mapRef,
    inputRef,
    isMapLoading,
    isMapError,
    isSearchResultPending,
    handleInitMap,
    handleInputSearchClick,
    handleLoadMarkers,
    handleLoadUserMarker,
    handleSelectStoreFromList,
  };
};
